import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BASE_URL, resolveUrl } from '../utils/api';
import { NotificationContext } from '../components/Layout';
import { getTheme } from '../utils/theme';

const theme = getTheme();

const COUNTRIES = ['Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh','Belgium','Brazil','Canada','Chile','China','Colombia','Croatia','Czech Republic','Denmark','Egypt','Ethiopia','Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Japan','Jordan','Kenya','Malaysia','Mexico','Morocco','Netherlands','New Zealand','Nigeria','Norway','Pakistan','Peru','Philippines','Poland','Portugal','Romania','Russia','Saudi Arabia','Serbia','Singapore','South Africa','South Korea','Spain','Sri Lanka','Sweden','Switzerland','Thailand','Turkey','Ukraine','United Arab Emirates','United Kingdom','United States','Vietnam','Other'];

function Avatar({ url, name, size = 80 }) {
  if (url) return <img src={url} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${theme.colors.primary}` }} onError={e => e.target.style.display='none'} />;
  return <div style={{ width: size, height: size, borderRadius: '50%', background: theme.colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: size * 0.38, border: `3px solid ${theme.colors.primaryHover}` }}>{name?.[0]?.toUpperCase() || '?'}</div>;
}

// Always store absolute avatar URL
function storeAvatarUrl(url) {
  if (!url) return;
  const abs = url.startsWith('http') ? url : `${BASE_URL.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
  localStorage.setItem('userAvatar', abs);
  return abs;
}

export default function Profile() {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext) || {};
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [pwForm, setPwForm] = useState({ current_password: '', new_password1: '', new_password2: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const avatarRef = useRef(null);
  const isGuest = localStorage.getItem('isGuest') === 'true';

  useEffect(() => {
    if (!form.username || form.username === profile?.username) {
      setUsernameStatus(null);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await api.get(`/users/check-username/?username=${form.username}`);
        setUsernameStatus({ available: res.data.is_available, suggestions: res.data.suggestions });
      } catch (err) {
        setUsernameStatus(null);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [form.username, profile?.username]);

  useEffect(() => {
    api.get('/update_profile/').then(res => {
      const u = res.data.user;
      setProfile(u);
      setForm({ username: u.username, email: u.email || '', first_name: u.first_name || '', last_name: u.last_name || '', bio: u.bio || '', gender: u.gender || '', mobile: u.mobile || '', country: u.country || '', address: u.address || '' });
      if (u.avatar) {
        const abs = storeAvatarUrl(u.avatar);
        setAvatarPreview(abs || resolveUrl(u.avatar));
      }
    }).catch(() => {});
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const LIMIT = 1 * 1024 * 1024; // 1MB
    if (file.size > LIMIT) {
      showNotification?.('Avatar file too large. Max limit is 1MB.', 5000);
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const saveProfile = async () => {
    setSaving(true); setMsg({ text: '', ok: true });
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (avatarFile) fd.append('avatar', avatarFile);
      const res = await api.put('/update_profile/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        const u = res.data.user;
        setProfile(u);
        localStorage.setItem('username', u.username);
        if (u.avatar) {
          const abs = storeAvatarUrl(u.avatar);
          setAvatarPreview(abs || resolveUrl(u.avatar));
        }
        setMsg({ text: '✓ Profile saved!', ok: true });
        showNotification?.('Profile updated!');
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Failed to save', ok: false });
    } finally { setSaving(false); }
  };

  const savePassword = async () => {
    setSaving(true); setMsg({ text: '', ok: true });
    try {
      const res = await api.post('/change_password/', pwForm);
      if (res.data.success) {
        setMsg({ text: '✓ Password changed!', ok: true });
        setPwForm({ current_password: '', new_password1: '', new_password2: '' });
        if (isGuest) localStorage.setItem('isGuest', 'false');
      } else {
        setMsg({ text: res.data.error, ok: false });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Failed', ok: false });
    } finally { setSaving(false); }
  };

  if (!profile) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>Loading...</div>;

  return (
    // FIX: overflowY:auto with dvh height to show full page on small screens
    <div style={S.root}>
      <div style={S.container}>
        <div style={S.header}>
          <button style={S.backBtn} onClick={() => navigate(-1)}>← Back</button>
          <h1 style={S.title}>My Profile</h1>
        </div>

        <div style={S.avatarSection}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Avatar url={avatarPreview} name={profile.username} size={90} />
            <button style={S.avatarEditBtn} onClick={() => avatarRef.current?.click()} title="Change photo">📷</button>
          </div>
          <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          <div>
            <div style={S.profileName}>{profile.username}</div>
            {isGuest && <div style={S.guestBadge}>👤 Guest Account</div>}
          </div>
        </div>

        {isGuest && (
          <div style={S.guestBanner}>
            ⚠️ You're a guest. <strong>Set a password</strong> to keep your account.
            <button style={S.setPasswordBtn} onClick={() => setTab('password')}>Set Password →</button>
          </div>
        )}

        <div style={S.tabs}>
          {['profile', 'password'].map(t => (
            <button key={t} style={{ ...S.tab, ...(tab === t ? S.activeTab : {}) }} onClick={() => { setTab(t); setMsg({ text: '', ok: true }); }}>
              {t === 'profile' ? '👤 Profile' : '🔒 Password'}
            </button>
          ))}
        </div>

        {msg.text && <div style={{ ...S.msgBanner, background: msg.ok ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: msg.ok ? '#4ade80' : '#f87171', border: `1px solid ${msg.ok ? '#4ade80' : '#f87171'}` }}>{msg.text}</div>}

        {tab === 'profile' && (
          <div style={S.formGrid}>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Username *" value={form.username} onChange={v => setForm(f => ({ ...f, username: v.replace(/ /g, '_') }))} />
              {usernameStatus && (
                <div style={{ fontSize: 11, marginTop: 4, color: usernameStatus.available ? '#4ade80' : '#f87171' }}>
                  {usernameStatus.available ? 'Username is available' : 'Username is taken.'}
                  {!usernameStatus.available && usernameStatus.suggestions?.length > 0 && (
                    <div style={{ marginTop: 2, color: '#94a3b8' }}>
                      Suggestions:{' '}
                      {usernameStatus.suggestions.map(s => (
                        <span key={s} onClick={() => setForm(f => ({...f, username: s}))} style={{ cursor: 'pointer', color: theme.colors.primary, marginRight: 6 }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Field label="Email (optional)" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} type="email" />
            <Field label="First Name" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} />
            <Field label="Last Name" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} />
            <div style={{ gridColumn: '1/-1' }}>
              <label style={S.label}>Bio</label>
              <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Tell people about yourself..." maxLength={500} />
            </div>
            <div>
              <label style={S.label}>Gender (optional)</label>
              <select style={S.input} value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                <option value="">Prefer not to say</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Field label="Mobile (optional)" value={form.mobile} onChange={v => setForm(f => ({ ...f, mobile: v }))} type="tel" />
            <div>
              <label style={S.label}>Country (optional)</label>
              <select style={S.input} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}>
                <option value="">Select country...</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={S.label}>Address (optional)</label>
              <textarea style={{ ...S.input, minHeight: 64, resize: 'vertical' }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Your address..." maxLength={300} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <button style={S.saveBtn} onClick={saveProfile} disabled={saving}>{saving ? 'Saving...' : '💾 Save Profile'}</button>
            </div>
          </div>
        )}

        {tab === 'password' && (
          <div style={S.formGrid}>
            {!isGuest && (
              <div style={{ gridColumn: '1/-1' }}>
                <Field label="Current Password" value={pwForm.current_password} onChange={v => setPwForm(f => ({ ...f, current_password: v }))} type="password" />
              </div>
            )}
            {isGuest && <div style={{ gridColumn: '1/-1', color: '#94a3b8', fontSize: 11 }}>Choose a password to convert your guest account to a permanent one.</div>}
            <Field label="New Password" value={pwForm.new_password1} onChange={v => setPwForm(f => ({ ...f, new_password1: v }))} type="password" />
            <Field label="Confirm Password" value={pwForm.new_password2} onChange={v => setPwForm(f => ({ ...f, new_password2: v }))} type="password" />
            <div style={{ gridColumn: '1/-1' }}>
              <button style={S.saveBtn} onClick={savePassword} disabled={saving}>{saving ? 'Saving...' : '🔒 ' + (isGuest ? 'Set Password' : 'Change Password')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <label style={{ display:'block', fontSize: 10, color:'#64748b', marginBottom:6, fontWeight:500 }}>{label}</label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          type={isPassword ? (showPw ? 'text' : 'password') : type}
          style={{ ...S.input, paddingRight: isPassword ? 40 : undefined, width: '100%', boxSizing: 'border-box' }}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw(s => !s)}
            style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#64748b', display:'flex', alignItems:'center', padding:0, lineHeight:1 }}
            tabIndex={-1}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

const S = {
  // FIX: overflowY:auto ensures full page visible on small screens
  root: { flex: 1, overflowY: 'auto', background: '#0f0f13', padding: '0 0 60px' },
  container: { maxWidth: 680, margin: '0 auto', padding: '0 16px' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '20px 0 16px' },
  backBtn: { background: 'none', border: 'none', color: theme.colors.primaryHover, cursor: 'pointer', fontSize: 13, padding: 0 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#e2e8f0' },
  avatarSection: { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, padding: '16px', background: '#16161d', borderRadius: 16, border: '1px solid #2d2d3d' },
  avatarEditBtn: { position: 'absolute', bottom: 0, right: 0, background: theme.colors.primaryHover, border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  profileName: { fontSize: 16, fontWeight: 700, color: '#e2e8f0' },
  guestBadge: { fontSize: 10, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', borderRadius: 6, padding: '3px 8px', marginTop: 4, display: 'inline-block' },
  guestBanner: { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#fbbf24', fontSize: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  setPasswordBtn: { background: '#f59e0b', border: 'none', color: '#000', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  tabs: { display: 'flex', gap: 8, marginBottom: 20 },
  tab: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #2d2d3d', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  activeTab: { background: '#1e1e2e', color: theme.colors.primary, borderColor: theme.colors.primaryHover },
  msgBanner: { borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 },
  // FIX: single column on small screens
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
  label: { display: 'block', fontSize: 10, color: '#64748b', marginBottom: 6, fontWeight: 500 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #2d2d3d', background: '#1e1e2e', color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  saveBtn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: theme.colors.gradient, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};